import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  ResourceList,
  ResourceItem,
  Stack,
  Pagination,
  SkeletonBodyText,
  Spinner,
  EmptyState,
  Filters,
  TextStyle,
  DatePicker,
  Button,
} from '@shopify/polaris';

import moment from 'moment-timezone';
import ExcelJS from 'exceljs/dist/es5/exceljs.browser';
import { saveAs } from 'file-saver';

import axios from 'axios';

import provinceJapaneseNamesFunc from './util/provinceJapaneseNamesFunc';

const date = new Date();

function AuthVendor(props) {
  const renderData = props.vendorOrders;
  const nextButtonData = props.nextPageParameters;
  const previousButtonData = props.previousPageParameters;

  const [selectedItems, setSelectedItems] = useState([]);
  const [csvExportLoading, setCsvExportLoading] = useState(false);
  const [dateFilterButtonLoading, setDateFilterButtonLoading] = useState(false);

  const handleClearSearchBox = useCallback(() => props.searchBox.onChange(''), [
    props.searchBox,
  ]);

  function renderItem(item) {
    const { id, name, created_at, line_items } = item;

    const productNames = line_items.map((singleItem) => (
      <span key={singleItem.id}>
        {singleItem.name} <br />
      </span>
    ));

    return (
      <ResourceItem
        verticalAlignment="center"
        id={id}
        onClick={() => {
          window.open(
            `https://${props.shopName}/admin/orders/${id}`,
            '__blank'
          );
          // props.setSelectedOrder(item);
        }}
        accessibilityLabel={`View details for ${name}`}
      >
        {props.paginationLoading ? (
          <SkeletonBodyText lines={1} />
        ) : (
          <Stack distribution="center" vertical={true} spacing="tight">
            <Stack.Item fill>
              <Stack>
                <Stack.Item fill>
                  <p style={{ color: 'dodgerblue' }}>{name}</p>
                </Stack.Item>
                <Stack.Item>
                  <p>
                    {moment(new Date(created_at)).tz('Japan').format('lll')}
                  </p>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <p>{productNames}</p>
            </Stack.Item>
          </Stack>
        )}
      </ResourceItem>
    );
  }

  const exportHandler = useCallback(async () => {
    setCsvExportLoading(true);
    const selectOrders = selectedItems.map((e) => {
      return renderData.find((singleData) => singleData.id === e);
    });

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('orders_export');

    const deliveryDateAttributes = ['配送日', '配送希望日'];
    const deliveryTimeAttributes = ['時間帯'];

    const sheetColumns = [{ header: '受注id', key: 'name' }];

    // want to add Shipping Address at the last

    sheetColumns.push({ header: '顧客名', key: 'billing_name' });
    sheetColumns.push({
      header: '配送元電話番号',
      key: 'billing_address_phone',
    });
    sheetColumns.push({ header: '配送元郵便番号', key: 'billing_zip' });
    sheetColumns.push({
      header: '配送元都道府県',
      key: 'billing_province',
    });
    sheetColumns.push({
      header: '配送元住所1',
      key: 'billing_address1',
    });
    sheetColumns.push({
      header: '配送元住所2',
      key: 'billing_address2',
    });

    sheetColumns.push({ header: '配送先名前', key: 'shipping_name' });
    sheetColumns.push({
      header: '配送先電話番号',
      key: 'shipping_address_phone',
    });
    sheetColumns.push({ header: '配送先郵便番号', key: 'shipping_zip' });
    sheetColumns.push({
      header: '配送先都道府県',
      key: 'shipping_province',
    });
    sheetColumns.push({
      header: '配送先住所1',
      key: 'shipping_address1',
    });
    sheetColumns.push({
      header: '配送先住所2',
      key: 'shipping_address2',
    });

    sheetColumns.push({
      header: 'お支払合計',
      key: 'total_price',
    });
    sheetColumns.push({
      header: '支払方法',
      key: 'payment_method_state',
    });
    sheetColumns.push({
      header: '配送日',
      key: 'delivery_date',
    });
    sheetColumns.push({
      header: '配送時間',
      key: 'delivery_time',
    });

    sheetColumns.push({
      header: '品名',
      key: 'fixed_text1',
    });
    sheetColumns.push({
      header: '荷扱い',
      key: 'fixed_text2',
    });

    sheet.columns = sheetColumns;

    selectOrders.forEach((singleOrder) => {
      const deliveryTimeValue = singleOrder.note_attributes.find((e) =>
        deliveryTimeAttributes.includes(e.name)
      )?.value;

      const data = {
        name: singleOrder.name,

        shipping_name: `${singleOrder?.shipping_address?.last_name || ''} ${
          singleOrder?.shipping_address?.first_name || ''
        }`,
        shipping_address1: `${singleOrder?.shipping_address?.city || ''} ${
          singleOrder?.shipping_address?.address1 || ''
        }`,
        shipping_address2: singleOrder?.shipping_address?.address2,
        shipping_company: singleOrder?.shipping_address?.company,
        shipping_zip: singleOrder?.shipping_address?.zip,
        shipping_province: provinceJapaneseNamesFunc(
          singleOrder?.shipping_address?.province
        ),
        shipping_address_phone: singleOrder?.shipping_address?.phone,
        shipping_address_country: singleOrder?.shipping_address?.country,
        total_price: singleOrder?.total_price,
        payment_method_state: singleOrder.payment_gateway_names.includes(
          'Cash on Delivery (COD)'
        )
          ? '2'
          : '0',
        delivery_date:
          singleOrder.note_attributes
            .find((e) => deliveryDateAttributes.includes(e.name))
            ?.value.replace(/-/g, '/') || '',
        delivery_time: deliveryTimeValue
          ? (deliveryTimeValue.split('-')[0]?.split(':')[0] || '') +
            (deliveryTimeValue.split('-')[1]?.split(':')[0] || '')
          : '',
        fixed_text1: 'ジャムなど食品類',
        fixed_text2: 'ワレモノ注意',
      };

      if (
        `${singleOrder?.shipping_address?.province} ${singleOrder?.shipping_address?.city} ${singleOrder?.shipping_address?.street} ${singleOrder?.shipping_address?.street}` !==
        `${singleOrder?.billing_address?.province} ${singleOrder?.billing_address?.city} ${singleOrder?.billing_address?.street} ${singleOrder?.billing_address?.street}`
      ) {
        data.billing_name = `${singleOrder?.billing_address?.last_name || ''} ${
          singleOrder?.billing_address?.first_name || ''
        }`;
        data.billing_address_phone = singleOrder?.billing_address?.phone;
        data.billing_zip = singleOrder?.billing_address?.zip;
        data.billing_province = provinceJapaneseNamesFunc(
          singleOrder?.billing_address?.province
        );
        data.billing_address1 = `${singleOrder?.billing_address?.city || ''} ${
          singleOrder?.billing_address?.address1 || ''
        }`;
        data.billing_address2 = singleOrder?.billing_address?.address2;
      } else {
        data.billing_name = '瀬戸内ジャムズガーデン';
        data.billing_address_phone = '0820-73-0002';
        data.billing_zip = '742-2804';
        data.billing_province = '山口県';
        data.billing_address1 = '大島郡周防大島町';
        data.billing_address2 = '日前331-8';
      }

      sheet.addRow(data);
    });

    const sheetBuf = await workbook.csv.writeBuffer();
    const nowDate = new Date();
    saveAs(
      new Blob([sheetBuf]),
      `Shopify_${nowDate.getFullYear()}_${
        nowDate.getMonth() + 1
      }_${nowDate.getDate()}__${nowDate.getHours()}_${nowDate.getMinutes()}.csv`
    );
    setCsvExportLoading(false);

    axios
      .post('/add_tags_to_order', {
        shopName: props.shopName,
        accessToken: props.accessToken,
        orderIds: selectOrders.map((e) => e.id),
      })
      .then(({ data }) => {
        console.log(data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [props.accessToken, props.shopName, renderData, selectedItems]);

  useEffect(() => {
    setSelectedItems([]);
  }, [renderData]);

  const handleQueryValueRemove = useCallback(
    () => props.searchBox.onChange(''),
    [props.searchBox]
  );

  const [{ month: statingMonth, year: statingYear }, setStatingDate] = useState(
    {
      month: date.getMonth(),
      year: date.getFullYear(),
    }
  );
  const [selectedStatingDates, setSelectedStatingDates] = useState({
    start: new Date(),
    end: new Date(),
  });

  const handleStatingMonthChange = useCallback(
    (month, year) => setStatingDate({ month, year }),
    []
  );

  const [{ month: endingMonth, year: endingYear }, setEndingDate] = useState({
    month: date.getMonth(),
    year: date.getFullYear(),
  });
  const [selectedEndingDates, setSelectedEndingDates] = useState({
    start: new Date(),
    end: new Date(),
  });

  const handleEndingMonthChange = useCallback(
    (month, year) => setEndingDate({ month, year }),
    []
  );

  const dateFilterHandler = useCallback(() => {
    const formattedStatingDates = `${selectedStatingDates.start.getFullYear()}-${
      selectedStatingDates.start.getMonth() + 1
    }-${selectedStatingDates.start.getDate()}`;
    const formattedEndingDates = `${selectedEndingDates.start.getFullYear()}-${
      selectedEndingDates.start.getMonth() + 1
    }-${selectedEndingDates.start.getDate() + 1}`;

    // add dates flags
    const key = 'dateRange';
    props.setAppliedFilters([
      {
        key,
        label: `${moment(selectedStatingDates.start).format('ll')}-${moment(
          selectedEndingDates.start
        ).format('ll')}`,
        onRemove: () => {
          props.setPaginationLoading(true);
          props.fetchOrdersData();
          props.setAppliedFilters((appliedFilters) =>
            appliedFilters.filter((e) => e.key !== key)
          );
        },
      },
    ]);

    // fetch the date range filter data
    setDateFilterButtonLoading(true);
    props.setPaginationLoading(true);
    props.fetchOrdersData(
      null,
      null,
      formattedStatingDates,
      formattedEndingDates,
      () => {
        setDateFilterButtonLoading(false);
      }
    );
  }, [props, selectedStatingDates.start, selectedEndingDates.start]);

  const filters = [
    {
      key: 'dateFilter',
      label: 'Date Filter',
      filter: (
        <>
          <TextStyle variation="strong">Starting</TextStyle>
          <br />
          <DatePicker
            month={statingMonth}
            year={statingYear}
            onChange={setSelectedStatingDates}
            onMonthChange={handleStatingMonthChange}
            selected={selectedStatingDates}
          />
          <br />

          <TextStyle variation="strong">Ending</TextStyle>
          <DatePicker
            month={endingMonth}
            year={endingYear}
            onChange={setSelectedEndingDates}
            onMonthChange={handleEndingMonthChange}
            selected={selectedEndingDates}
          />
          <br />
          <Button
            fullWidth
            onClick={dateFilterHandler}
            loading={dateFilterButtonLoading}
          >
            Filter
          </Button>
        </>
      ),
      shortcut: true,
    },
  ];

  return (
    <>
      <Card>
        <Card.Section title="Unfulfilled Order List">
          <ResourceList
            resourceName={{ singular: 'order', plural: 'orders' }}
            items={renderData}
            renderItem={renderItem}
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
            filterControl={
              <Filters
                queryValue={props.searchBox.value}
                onQueryChange={props.searchBox.onChange}
                onQueryClear={handleQueryValueRemove}
                queryPlaceholder="Search By Order Number"
                filters={filters}
                appliedFilters={props.appliedFilters}
              />
            }
            promotedBulkActions={[
              {
                content: 'Export to CSV',
                onAction: exportHandler,
                disabled: csvExportLoading,
              },
            ]}
            emptyState={
              <EmptyState
                heading="No orders found"
                action={{
                  content: 'View all orders',
                  onAction: handleClearSearchBox,
                }}
              >
                <p>Try with another order number</p>
              </EmptyState>
            }
          />
        </Card.Section>
      </Card>

      <br />

      <Stack distribution="center">
        {props.paginationLoading ? (
          <Spinner color="teal" size="small" />
        ) : (
          <Pagination
            hasPrevious={Boolean(previousButtonData)}
            onPrevious={() => {
              window.scroll(0, 0);
              props.setPaginationLoading(true);
              props.fetchOrdersData(previousButtonData);
            }}
            hasNext={Boolean(nextButtonData)}
            onNext={() => {
              window.scroll(0, 0);
              props.setPaginationLoading(true);
              props.fetchOrdersData(nextButtonData);
            }}
          />
        )}
      </Stack>
    </>
  );
}

export default AuthVendor;
